import axios from "axios";
import { defaultSettings } from "contexts/appsettings/defaultSettings";
import { LoaderFunction, LoaderFunctionArgs } from "react-router-dom";
import { REACT_APP_BACKENDURL } from "../../../config";
import { getSubscriptionStatusLoaderData } from "../comments/getSubscriptionStatusLoaderData";
import { eTlfItemType } from "../enums";
import { getHierarchyLoaderData, HierarchyLoaderData } from "../hierarchy/hierarchyLoader";
import { hydrateArsTableBody } from "./hydrateArsTableBody";
import { TlfItemReadDto } from "./TlfItemReadDto";
import { tlfPreProcess } from "./tlfPreProcess";

/**
 * Loader data containing only the TLF item and subscription status.
 */
export interface TlfOnlyLoaderData {
  /** The TLF item data, or undefined if not found. */
  tlfItem: TlfItemReadDto | undefined;
  /** Whether the current user is subscribed to the TLF. */
  isTlfSubscribed: boolean;
  /** Flag to indicate whether the TLF item has been saved to the database or not. */
  isTlfNew: boolean;
}

/**
 * Loader data including hierarchy information and TLF item data.
 */
export interface TlfLoaderData extends HierarchyLoaderData, TlfOnlyLoaderData {}

/**
 * Construct the URL for the TLF loader API endpoint from route params.
 *
 * @param params - Loader function params containing identifiers for the TLF
 * @returns A full backend URL or null when params are insufficient
 */
export const getTlfLoaderUrl = (params: LoaderFunctionArgs["params"]): string | null => {
  const backEndUrl = REACT_APP_BACKENDURL.includes("_REACT_APP_BACKENDURL_")
    ? defaultSettings.backEndUrl
    : REACT_APP_BACKENDURL;

  return params.azsolId !== undefined && params.version !== undefined
    ? `${backEndUrl}TlfItems/${params.azsolId}/${params.version}`
    : params.studyTag && params.reportName && params.tocNumber
      ? `${backEndUrl}TlfItems/${params.studyTag}/${params.reportName}/${params.tocNumber}${params.tocMajorVersion ? `?version=${params.tocMajorVersion}` : ""}`
      : params.id
        ? `${backEndUrl}TlfItems/${params.id}`
        : null;
};

/**
 * Fetch the TLF item and subscription status for given params.
 *
 * @param params - Loader function params containing identifiers for the TLF
 * @returns TLF-only loader data including subscription status
 */
export const getTlfLoaderData = async (
  params: LoaderFunctionArgs["params"],
): Promise<TlfOnlyLoaderData> => {
  const url = getTlfLoaderUrl(params);
  if (!url) return { tlfItem: undefined, isTlfSubscribed: false, isTlfNew: false };
  const tlfLoaderData = (await axios.get(url)).data as TlfItemReadDto | undefined;

  const subscriptionStatusLoaderData = tlfLoaderData
    ? await getSubscriptionStatusLoaderData({
        params: {
          entityType: "TlfItem",
          entityId: tlfLoaderData.id as string,
        },
      })
    : undefined;

  const ret: TlfOnlyLoaderData = {
    tlfItem: tlfLoaderData !== undefined ? tlfPreProcess(tlfLoaderData) : undefined,
    isTlfSubscribed: subscriptionStatusLoaderData?.isSubscribed ?? false,
    isTlfNew: false,
  };
  return ret;
};

/**
 * Loader that returns TLF item and hierarchy data required by the TLF page.
 *
 * @param props - Loader args provided by react-router
 * @param props.params - Route params used to identify the TLF
 * @param props.request - The request object, used to access the AbortController signal
 */
export const tlfLoader: LoaderFunction = async ({
  params,
  request,
}: LoaderFunctionArgs): Promise<TlfLoaderData> => {
  const hierarchyLoader = getHierarchyLoaderData(params, request.signal);
  const thisTlfLoader = getTlfLoaderData(params);

  const [tlfLoaderData, hierarchyLoaderData] = await Promise.all([thisTlfLoader, hierarchyLoader]);

  // Hydrate ARS items into ArsTable
  if (tlfLoaderData.tlfItem?.type === eTlfItemType.arsTable) {
    hydrateArsTableBody(
      [hierarchyLoaderData.hierarchy, ...hierarchyLoaderData.parentHierarchies],
      tlfLoaderData.tlfItem,
    );
  }

  return {
    ...hierarchyLoaderData,
    ...tlfLoaderData,
  };
};
